import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z as zod, z } from "zod";
import { useScreenNavigation } from "@/common/Contexts/ScreenNavigationContext";
import { useSellCarData } from "@/common/Contexts/SellCarDataContext";
import { useTranslation } from "@i18n";
import { useLang } from "@/common/Contexts/LanguageContext";
import { searchCities } from "@/pages/sell-my-car/utils/citySearchHelper";
import { Form } from "@/common/HOCs/rhf/form-provider/FormProvider";
import RHFTextField from "@/common/HOCs/rhf/text/RHFTextField";
import RHFInputPhoneNumber from "@/common/HOCs/rhf/phone-number/RHFInputPhoneNumber";
import ContactInfoLabel from "@/pages/sell-my-car/components/ContactInfoLabel";
import RHFDrawerSelect from "@/common/HOCs/rhf/drawer-select/RHFDrawerSelect";
import RHFDialogSelect from "@/common/HOCs/rhf/dialog-select/RHFDialogSelect";
import { getAllCities } from "@/pages/price-offer/price-offer-helper";
import RHFCheckbox from "@/common/HOCs/rhf/checkbox/RHFCheckbox";
import { CDN_LINK } from "@globalConfig";
import { DrawerPortal } from "@/components/Drawer";
import useBoolean from "@hooks/useBoolean";
import { Drawer } from "vaul";
import SellTradeTermsAr from "../../../../data/sell-trade-terms/ar.json";
import SellTradeTermsEn from "../../../../data/sell-trade-terms/en.json";
import { Icon } from "@syarahtech/ui";
import Markdown from "markdown-to-jsx";
import { Conditional } from "@componentsShared/Conditional/Conditional";
import useIsMobile from "@hooks/useIsMobile";
import { Then } from "@componentsShared/Conditional/Then";
import { Else } from "@componentsShared/Conditional/Else";
import { Dialog, DialogContent } from "@/components/Dialog";
import { useSession } from "@/_MiniApps/auth/AuthContext";
import OtpVerification from "@/_MiniApps/auth/ui/OTP/OtpVerification";
import { getStep, sendSellTrade, submitSellTradeStep } from "@/apis/sell-my-car";
import { useNavigate, useSearchParams } from "react-router-dom";
import FlowButton from "@/pages/sell-my-car/components/FlowButton";
import useHelpers from "@hooks/useHelpers";
import { SELL_CAR_SCREENS } from "@/pages/sell-my-car/constants/screens";
import { toast } from "sonner";
import FLowLoadingSpinner from "@/pages/sell-my-car/components/FLowLoadingSpinner";
import FLowFixedContainer from "@/pages/sell-my-car/components/FLowFixedContainer";
import { getPlatformInfo, toSnakeCase } from "@/utils/GlobalJavascriptFunction";
import { trackEvents } from "@/common/events/events";
import { twMerge } from "tailwind-merge";
var ContactInfoSchema = z.object({
  name: z.string().min(1, "fields/required").min(3, "sell_car_v2_validation/full_name_length").refine(function (value) {
    var regexExp = /^[a-zA-Zأ-ي\s]+$/;
    return regexExp.test(value);
  }, {
    message: "sell_car_v2_validation/special_characters"
  }),
  phone: z.string().min(1, "fields/required").superRefine(function (value, ctx) {
    if (value.startsWith('05')) {
      if (value.length > 3 && (value.length !== 10 || !/^05\d{8}$/.test(value))) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: "sell_car_v2_validation/phone_with_05_must_be_10"
        });
      }
    } else if (value.startsWith('5')) {
      if (value.length > 3 && (value.length !== 9 || !/^5\d{8}$/.test(value))) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: "sell_car_v2_validation/phone_with_5_must_be_9"
        });
      }
    } else if (value.startsWith('0') && value.length < 3) {
      return;
    } else {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: "sell_car_v2_validation/phone_must_start_with_5"
      });
    }
  }),
  city: z.object({
    id: z.string(),
    name_en: z.string(),
    name_ar: z.string()
  }).nullable().refine(function (val) {
    return val !== null;
  }, {
    message: "fields/required"
  }),
  send_whatsapp_notification: zod["boolean"]().optional()
});
var ContactInfoScreen = function ContactInfoScreen() {
  var _carData$draftOrder2, _carData$selection2;
  var _useScreenNavigation = useScreenNavigation(),
    actions = _useScreenNavigation.actions;
  var _useSellCarData = useSellCarData(),
    carData = _useSellCarData.state,
    dataActions = _useSellCarData.actions;
  var setIsOtpVisible = dataActions.setIsOtpVisible;
  var _useTranslation = useTranslation(),
    tShort = _useTranslation.tShort;
  var lang = useLang();
  var navigate = useNavigate();
  var _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 1),
    searchParams = _useSearchParams2[0];
  var termsModal = useBoolean();
  var isMobile = useIsMobile();
  var _useSession = useSession(),
    isLoggedIn = _useSession.isLoggedIn,
    userData = _useSession.user;
  // OTP and submission state
  var _useState = useState(false),
    showOtp = _useState[0],
    setShowOtp = _useState[1];
  var _useState2 = useState(false),
    submissionLoading = _useState2[0],
    setSubmissionLoading = _useState2[1];
  var _useState3 = useState(null),
    pendingFormData = _useState3[0],
    setPendingFormData = _useState3[1];

  // Sync OTP visibility with context for FlowStepMeta to hide tags
  useEffect(function () {
    setIsOtpVisible(showOtp);
    return function () {
      return setIsOtpVisible(false);
    };
  }, [showOtp, setIsOtpVisible]);
  var _useHelpers = useHelpers(),
    navigateTo = _useHelpers.navigateTo;
  var _useHelpers2 = useHelpers(),
    createUrl = _useHelpers2.createUrl;
  // Check if there's a request_id in URL
  var requestId = searchParams.get("request_id");
  var termsContent = useMemo(function () {
    if (lang === 'ar') {
      return {
        title: SellTradeTermsAr.title,
        description: SellTradeTermsAr.description
      };
    }
    return {
      title: SellTradeTermsEn.title,
      description: SellTradeTermsEn.description
    };
  }, [lang]);

  // Helper function to track sell_car_contact_submit event
  var trackSellCarContactSubmit = function trackSellCarContactSubmit(formData, status, failReason) {
    var _formData$city, _carData$selection$ma, _carData$selection$mo, _carData$selection$ex, _carData$selection$ye, _carData$selection$od;
    var cityName = (_formData$city = formData.city) === null || _formData$city === void 0 ? void 0 : _formData$city.name_ar;
    trackEvents("sell_car_contact_submit", {
      car_make: ((_carData$selection$ma = carData.selection.make) === null || _carData$selection$ma === void 0 ? void 0 : _carData$selection$ma.name) || "",
      car_model: ((_carData$selection$mo = carData.selection.model) === null || _carData$selection$mo === void 0 ? void 0 : _carData$selection$mo.name) || "",
      car_trim: ((_carData$selection$ex = carData.selection.extension) === null || _carData$selection$ex === void 0 ? void 0 : _carData$selection$ex.name) || "",
      car_year: ((_carData$selection$ye = carData.selection.year) === null || _carData$selection$ye === void 0 ? void 0 : _carData$selection$ye.toString()) || "",
      car_odometer_range: ((_carData$selection$od = carData.selection.odometerRange) === null || _carData$selection$od === void 0 ? void 0 : _carData$selection$od.range_ar) || "",
      ownership_type: toSnakeCase(carData.selection.ownership || ""),
      car_city: cityName || "",
      inspection_city: cityName || "",
      status: status,
      fail_reason: failReason || "",
      platform: getPlatformInfo(),
      version: "3"
    });
  };

  // Get approved cities for inspection
  var cities = useMemo(function () {
    return getAllCities();
  }, []);
  var defaultValues = useMemo(function () {
    var _carData$draftOrder$c, _carData$draftOrder, _carData$draftOrder$c2, _carData$draftOrder$c3, _carData$draftOrder$c4, _carData$selection;
    return {
      name: (carData === null || carData === void 0 ? void 0 : (_carData$draftOrder$c = carData.draftOrder.contactInfo) === null || _carData$draftOrder$c === void 0 ? void 0 : _carData$draftOrder$c.name) || (carData === null || carData === void 0 ? void 0 : (_carData$draftOrder = carData.draftOrder) === null || _carData$draftOrder === void 0 ? void 0 : (_carData$draftOrder$c2 = _carData$draftOrder.currentStep) === null || _carData$draftOrder$c2 === void 0 ? void 0 : (_carData$draftOrder$c3 = _carData$draftOrder$c2.data) === null || _carData$draftOrder$c3 === void 0 ? void 0 : _carData$draftOrder$c3.seller_name) || "",
      phone: (userData === null || userData === void 0 ? void 0 : userData.phone_number) || (carData === null || carData === void 0 ? void 0 : (_carData$draftOrder$c4 = carData.draftOrder.contactInfo) === null || _carData$draftOrder$c4 === void 0 ? void 0 : _carData$draftOrder$c4.phone) || "",
      city: (carData === null || carData === void 0 ? void 0 : (_carData$selection = carData.selection) === null || _carData$selection === void 0 ? void 0 : _carData$selection.selectedCityForInspection) || null,
      send_whatsapp_notification: true
    };
  }, [userData === null || userData === void 0 ? void 0 : userData.phone_number, carData === null || carData === void 0 ? void 0 : (_carData$draftOrder2 = carData.draftOrder) === null || _carData$draftOrder2 === void 0 ? void 0 : _carData$draftOrder2.contactInfo, carData === null || carData === void 0 ? void 0 : (_carData$selection2 = carData.selection) === null || _carData$selection2 === void 0 ? void 0 : _carData$selection2.selectedCityForInspection]);
  var methods = useForm({
    mode: "all",
    resolver: zodResolver(ContactInfoSchema),
    defaultValues: defaultValues
  });
  var handleSubmit = methods.handleSubmit,
    _methods$formState = methods.formState,
    isSubmitting = _methods$formState.isSubmitting,
    isValid = _methods$formState.isValid,
    watch = methods.watch,
    trigger = methods.trigger;

  // Trigger validation on mount if there are pre-filled values
  useEffect(function () {
    if (defaultValues.name) {
      trigger("name");
    }
  }, []);

  // Watch the selected city
  var selectedCity = watch("city");
  useEffect(function () {
    if (selectedCity) {
      dataActions.setCityForInspection(selectedCity);
    }
  }, [selectedCity]);

  // Function to update contact info when request_id exists
  var handleUpdateContactInfo = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(formData) {
      var _formData$city2, submitData, res, _stepResponse$data, _stepData$current_ste, landingPageParam, getStepParams, stepResponse, stepData, currentStepKey, urlParams, newURL, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            if (requestId) {
              _context.n = 1;
              break;
            }
            return _context.a(2);
          case 1:
            setSubmissionLoading(true);
            _context.p = 2;
            submitData = {
              request_id: requestId,
              seller_name: formData.name,
              city_id: parseInt(((_formData$city2 = formData.city) === null || _formData$city2 === void 0 ? void 0 : _formData$city2.id) || "0")
            };
            _context.n = 3;
            return submitSellTradeStep(submitData);
          case 3:
            res = _context.v;
            if (!(res !== null && res !== void 0 && res.data)) {
              _context.n = 5;
              break;
            }
            // Get next step and navigate
            landingPageParam = searchParams.get("landing_page");
            getStepParams = {
              request_id: requestId
            };
            if (landingPageParam === "true") {
              getStepParams.landing_page = true;
            }
            _context.n = 4;
            return getStep(getStepParams);
          case 4:
            stepResponse = _context.v;
            stepData = stepResponse === null || stepResponse === void 0 ? void 0 : (_stepResponse$data = stepResponse.data) === null || _stepResponse$data === void 0 ? void 0 : _stepResponse$data.data;
            if (stepData) {
              dataActions.setStepNavigation(stepData.previous_step || null, stepData.current_step || null);
            }
            currentStepKey = stepData === null || stepData === void 0 ? void 0 : (_stepData$current_ste = stepData.current_step) === null || _stepData$current_ste === void 0 ? void 0 : _stepData$current_ste.step_key;
            if (currentStepKey) {
              urlParams = {
                request_id: requestId
              };
              if (landingPageParam === "true") {
                urlParams.landing_page = "true";
              }
              newURL = createUrl("/site/sell-my-car", urlParams);
              window.history.replaceState({}, null, newURL);
              actions.navigateToScreen(currentStepKey);
              navigate(newURL, {
                replace: true
              });
            }

            // Track success event
            trackSellCarContactSubmit(formData, "success");
          case 5:
            _context.n = 7;
            break;
          case 6:
            _context.p = 6;
            _t = _context.v;
            console.error("Update contact info error:", _t);
            alert("An error occurred while updating. Please try again.");
          case 7:
            _context.p = 7;
            setSubmissionLoading(false);
            setShowOtp(false);
            setPendingFormData(null);
            return _context.f(7);
          case 8:
            return _context.a(2);
        }
      }, _callee, null, [[2, 6, 7, 8]]);
    }));
    return function handleUpdateContactInfo(_x) {
      return _ref.apply(this, arguments);
    };
  }();

  // Function to call SendSellTrade API
  var handleSendSellTrade = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2(formData) {
      var _responseData$message, _formData$city4, _formData$city5, _formData$city6, _stepResponse$data2, _stepData$current_ste2;
      var _carData$selection$ma2, _carData$selection$mo2, _carData$selection$ex2, _carData$selection3, _carData$selection3$o, _formData$city3, res, _ref3, code, responseData, contactInfo, landingPageParam, getStepParams, stepResponse, stepData, currentStepKey, urlParams, newURL, _t2, _t3;
      return _regenerator().w(function (_context2) {
        while (1) switch (_context2.p = _context2.n) {
          case 0:
            if (!requestId) {
              _context2.n = 1;
              break;
            }
            return _context2.a(2, handleUpdateContactInfo(formData));
          case 1:
            setSubmissionLoading(true);
            _context2.p = 2;
            _context2.n = 3;
            return sendSellTrade({
              seller_name: formData.name,
              seller_mobile: formData.phone,
              car_make_id: ((_carData$selection$ma2 = carData.selection.make) === null || _carData$selection$ma2 === void 0 ? void 0 : _carData$selection$ma2.id) || 0,
              car_model_id: ((_carData$selection$mo2 = carData.selection.model) === null || _carData$selection$mo2 === void 0 ? void 0 : _carData$selection$mo2.id) || 0,
              car_trim_id: (_carData$selection$ex2 = carData.selection.extension) === null || _carData$selection$ex2 === void 0 ? void 0 : _carData$selection$ex2.id,
              year: carData.selection.year.toString(),
              odometer_range_id: carData === null || carData === void 0 ? void 0 : (_carData$selection3 = carData.selection) === null || _carData$selection3 === void 0 ? void 0 : (_carData$selection3$o = _carData$selection3.odometerRange) === null || _carData$selection3$o === void 0 ? void 0 : _carData$selection3$o.name,
              city_id: parseInt(((_formData$city3 = formData.city) === null || _formData$city3 === void 0 ? void 0 : _formData$city3.id) || "0"),
              send_whatsApp_notification: formData.send_whatsapp_notification ? "1" : "0",
              ownership_type: carData.selection.ownership
            });
          case 3:
            res = _context2.v;
            _ref3 = (res === null || res === void 0 ? void 0 : res.data) || {}, code = _ref3.code, responseData = _ref3.data;
            if (code == 200 && (responseData === null || responseData === void 0 ? void 0 : responseData.request_id) == null) {
              toast.error(responseData === null || responseData === void 0 ? void 0 : responseData.messageDescription, {
                position: "top-center"
              });
            }
            // Handle response
            _t2 = code;
            _context2.n = _t2 === 240 ? 4 : _t2 === 230 ? 5 : 6;
            break;
          case 4:
            trackSellCarContactSubmit(formData, "fail", "attempts_limit");
            alert((responseData === null || responseData === void 0 ? void 0 : (_responseData$message = responseData.message) === null || _responseData$message === void 0 ? void 0 : _responseData$message.title) || "Attempts limit exceeded");
            return _context2.a(3, 8);
          case 5:
            trackSellCarContactSubmit(formData, "fail", "not_eligible");
            navigateTo("/site/sell-my-car/rejected");
            return _context2.a(3, 8);
          case 6:
            // Save draft order
            contactInfo = {
              name: formData.name,
              phone: formData.phone,
              cityId: ((_formData$city4 = formData.city) === null || _formData$city4 === void 0 ? void 0 : _formData$city4.id) || "",
              cityName: lang === "ar" ? ((_formData$city5 = formData.city) === null || _formData$city5 === void 0 ? void 0 : _formData$city5.name_ar) || "" : ((_formData$city6 = formData.city) === null || _formData$city6 === void 0 ? void 0 : _formData$city6.name_en) || "",
              sendWhatsappNotification: formData.send_whatsapp_notification || false
            };
            dataActions.setDraftOrder(responseData === null || responseData === void 0 ? void 0 : responseData.request_id, contactInfo);

            // Get next step and navigate
            landingPageParam = searchParams.get("landing_page");
            getStepParams = {
              request_id: responseData === null || responseData === void 0 ? void 0 : responseData.request_id
            };
            if (landingPageParam === "true") {
              getStepParams.landing_page = true;
            }
            _context2.n = 7;
            return getStep(getStepParams);
          case 7:
            stepResponse = _context2.v;
            stepData = stepResponse === null || stepResponse === void 0 ? void 0 : (_stepResponse$data2 = stepResponse.data) === null || _stepResponse$data2 === void 0 ? void 0 : _stepResponse$data2.data;
            if (stepData) {
              dataActions.setStepNavigation(stepData.previous_step || null, stepData.current_step || null);
            }
            currentStepKey = stepData === null || stepData === void 0 ? void 0 : (_stepData$current_ste2 = stepData.current_step) === null || _stepData$current_ste2 === void 0 ? void 0 : _stepData$current_ste2.step_key;
            if (currentStepKey) {
              urlParams = {
                request_id: responseData === null || responseData === void 0 ? void 0 : responseData.request_id
              };
              if (landingPageParam === "true") {
                urlParams.landing_page = "true";
              }
              newURL = createUrl("/site/sell-my-car", urlParams);
              window.history.replaceState({}, null, newURL);
              actions.navigateToScreen(currentStepKey);
              // Navigate to the new URL
              navigate(newURL, {
                replace: true
              });
            }

            // Track success event
            trackSellCarContactSubmit(formData, "success");
            return _context2.a(3, 8);
          case 8:
            _context2.n = 10;
            break;
          case 9:
            _context2.p = 9;
            _t3 = _context2.v;
            console.error("Submission error:", _t3);
            alert("An error occurred. Please try again.");
          case 10:
            _context2.p = 10;
            setSubmissionLoading(false);
            setShowOtp(false);
            setPendingFormData(null);
            return _context2.f(10);
          case 11:
            return _context2.a(2);
        }
      }, _callee2, null, [[2, 9, 10, 11]]);
    }));
    return function handleSendSellTrade(_x2) {
      return _ref2.apply(this, arguments);
    };
  }();

  // Function to verify OTP and submit
  var handleVerifyOtp = /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee3() {
      return _regenerator().w(function (_context3) {
        while (1) switch (_context3.n) {
          case 0:
            if (!pendingFormData) {
              _context3.n = 1;
              break;
            }
            _context3.n = 1;
            return handleSendSellTrade(pendingFormData);
          case 1:
            return _context3.a(2);
        }
      }, _callee3);
    }));
    return function handleVerifyOtp() {
      return _ref4.apply(this, arguments);
    };
  }();

  // Normalize phone number to always start with 0
  var normalizePhone = function normalizePhone(phone) {
    return phone.startsWith('0') ? phone : "0".concat(phone);
  };

  // Main submit handler
  var onSubmit = handleSubmit(/*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee4(data) {
      var normalizedData, cityData, _normalizedData$city2, _normalizedData$city3, _normalizedData$city4, contactInfo;
      return _regenerator().w(function (_context4) {
        while (1) switch (_context4.n) {
          case 0:
            // Normalize phone number to always start with 0
            normalizedData = _objectSpread(_objectSpread({}, data), {}, {
              phone: normalizePhone(data.phone)
            }); // Check if selected city is approved for inspection
            if (!normalizedData.city) {
              _context4.n = 1;
              break;
            }
            cityData = cities.find(function (c) {
              var _normalizedData$city;
              return c.id === ((_normalizedData$city = normalizedData.city) === null || _normalizedData$city === void 0 ? void 0 : _normalizedData$city.id);
            });
            if (!(cityData && !cityData.approved_for_inspection)) {
              _context4.n = 1;
              break;
            }
            // Save form data before navigating away so it persists when coming back
            contactInfo = {
              name: normalizedData.name,
              phone: normalizedData.phone,
              cityId: ((_normalizedData$city2 = normalizedData.city) === null || _normalizedData$city2 === void 0 ? void 0 : _normalizedData$city2.id) || "",
              cityName: lang === "ar" ? ((_normalizedData$city3 = normalizedData.city) === null || _normalizedData$city3 === void 0 ? void 0 : _normalizedData$city3.name_ar) || "" : ((_normalizedData$city4 = normalizedData.city) === null || _normalizedData$city4 === void 0 ? void 0 : _normalizedData$city4.name_en) || "",
              sendWhatsappNotification: normalizedData.send_whatsapp_notification || false
            };
            dataActions.setContactInfo(contactInfo);

            // Track fail event - city not approved for inspection

            // Navigate to location not approved screen and stop submission
            actions.navigateToScreen(SELL_CAR_SCREENS.NOT_APPROVED_FOR_INSPECTION);
            return _context4.a(2);
          case 1:
            if (!isLoggedIn) {
              _context4.n = 3;
              break;
            }
            _context4.n = 2;
            return handleSendSellTrade(normalizedData);
          case 2:
            _context4.n = 4;
            break;
          case 3:
            // User not logged in - show OTP screen
            setPendingFormData(normalizedData);
            setShowOtp(true);
          case 4:
            return _context4.a(2);
        }
      }, _callee4);
    }));
    return function (_x3) {
      return _ref5.apply(this, arguments);
    };
  }());

  // If showing OTP, render standalone OTP screen
  if (showOtp && pendingFormData) {
    return /*#__PURE__*/React.createElement("div", {
      id: "sell-car-contact-otp-screen",
      className: "p-4 h-full relative"
    }, /*#__PURE__*/React.createElement(OtpVerification, {
      source: "sell_trade",
      after_verify_callback: handleVerifyOtp,
      phone_number: pendingFormData.phone,
      scenario: "signup",
      onEditNumber: function onEditNumber() {
        setShowOtp(false);
        setPendingFormData(null);
      },
      design: {
        className: "",
        btnText: tShort("phone_number_verification", {
          ns: "otp_screen"
        }),
        label: tShort("enter_code_sent_to", {
          ns: "otp_screen"
        })
      },
      LoaderComponent: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FLowLoadingSpinner, null))
    }));
  }
  return /*#__PURE__*/React.createElement("div", {
    id: "sell-car-contact-screen",
    className: "p-4 h-full relative"
  }, /*#__PURE__*/React.createElement(Form, {
    methods: methods,
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement("div", {
    id: "sell-car-contact-form",
    className: "space-y-4"
  }, /*#__PURE__*/React.createElement("div", {
    id: "sell-car-contact-name-field",
    className: "flex flex-col gap-2"
  }, /*#__PURE__*/React.createElement(ContactInfoLabel, null, tShort("sell_trade_v2/full_name", {
    ns: "web_sell_trade"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(RHFTextField, {
    name: "name",
    placeholder: tShort("sell_trade_v2/full_name_placeholder", {
      ns: "web_sell_trade"
    }),
    className: "w-full border border-solid border-gray-echo placeholder:text-sm placeholder:text-[#A3AFC0] text-base text-gray-text p-3 rounded-lg"
  }))), /*#__PURE__*/React.createElement("div", {
    id: "sell-car-contact-phone-field",
    className: "flex flex-col gap-2"
  }, /*#__PURE__*/React.createElement(ContactInfoLabel, null, tShort("sell_trade_v2/phone_label", {
    ns: "web_sell_trade"
  })), /*#__PURE__*/React.createElement(RHFInputPhoneNumber, {
    name: "phone",
    lang: lang == 'ar' ? "ar" : "en",
    placeholder: tShort("sell_trade_v2/phone_placeholder", {
      ns: "web_sell_trade"
    }),
    haveLabel: false,
    autoComplete: "off",
    disabled: isLoggedIn,
    maxLength: 10,
    className: "placeholder:!text-[#A3AFC0] placeholder:!text-base !text-base !text-gray-text disabled:bg-gray-100 disabled:cursor-not-allowed"
  })), /*#__PURE__*/React.createElement("div", {
    id: "sell-car-contact-city-field",
    className: "flex flex-col gap-2"
  }, /*#__PURE__*/React.createElement(ContactInfoLabel, null, tShort("sell_trade_v2/city_label", {
    ns: "web_sell_trade"
  })), /*#__PURE__*/React.createElement(Conditional, {
    condition: isMobile
  }, /*#__PURE__*/React.createElement(Then, null, /*#__PURE__*/React.createElement(RHFDrawerSelect, {
    name: "city",
    id: "sell-car-contact-city-select",
    options: cities,
    searchFunction: searchCities,
    getDisplayName: function getDisplayName(city) {
      return lang === "ar" ? city.name_ar : city.name_en;
    },
    placeholder: tShort("sell_trade_v2/city_placeholder", {
      ns: "web_sell_trade"
    }),
    searchPlaceholder: tShort("sell_trade_v2/search_cities", {
      ns: "web_sell_trade"
    }),
    drawerTitle: tShort("sell_trade_v2/select_city_title", {
      ns: "web_sell_trade"
    })
  })), /*#__PURE__*/React.createElement(Else, null, /*#__PURE__*/React.createElement(RHFDialogSelect, {
    name: "city",
    id: "sell-car-contact-city-select",
    options: cities,
    searchFunction: searchCities,
    getDisplayName: function getDisplayName(city) {
      return lang === "ar" ? city.name_ar : city.name_en;
    },
    placeholder: tShort("sell_trade_v2/city_placeholder", {
      ns: "web_sell_trade"
    }),
    searchPlaceholder: tShort("sell_trade_v2/search_cities", {
      ns: "web_sell_trade"
    }),
    dialogTitle: tShort("sell_trade_v2/select_city_title", {
      ns: "web_sell_trade"
    })
  })))), /*#__PURE__*/React.createElement("label", {
    id: "sell-car-contact-whatsapp-field",
    className: "flex items-center gap-3 cursor-pointer group"
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative inline-flex items-center [&_input[type=checkbox]]:!block [&_input[type=checkbox]]:!opacity-0 [&_input[type=checkbox]]:!absolute [&_input[type=checkbox]]:!w-11 [&_input[type=checkbox]]:!h-6 [&_input[type=checkbox]]:!cursor-pointer [&_input[type=checkbox]]:!z-10 [&_.boxDummy]:hidden [&_input[type=checkbox]:checked~div]:bg-primary [&_input[type=checkbox]:checked~div>div]:translate-x-5 [&_input[type=checkbox]:checked~div>div]:rtl:-translate-x-5 [&_input[type=checkbox]:checked~.checkbox-box]:!bg-primary"
  }, /*#__PURE__*/React.createElement(RHFCheckbox, {
    name: "send_whatsapp_notification",
    boxDummyClassName: "!bg-check-icon bg-no-repeat bg-position-center bg-contain checkbox-box"
  })), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-3"
  }, /*#__PURE__*/React.createElement("img", {
    id: "sell-car-contact-whatsapp-icon",
    src: CDN_LINK + "/assets/images/wpImg.svg",
    alt: "whatsapp",
    width: 24,
    height: 24,
    className: "flex-shrink-0"
  }), /*#__PURE__*/React.createElement("span", {
    id: "sell-car-contact-whatsapp-text",
    className: "text-sm text-gray-text"
  }, tShort("sell_trade_v2/receive_whats_notification", {
    ns: "web_sell_trade"
  })))), /*#__PURE__*/React.createElement(FLowFixedContainer, null, /*#__PURE__*/React.createElement("div", {
    className: "md:relative md:max-w-[340px] md:mx-auto md:gap-5 md:!mt-[90px]"
  }, /*#__PURE__*/React.createElement(FlowButton, {
    type: "button",
    disabled: isSubmitting || !isValid || submissionLoading,
    onClick: onSubmit,
    className: "w-full md:min-w-[360px]"
  }, /*#__PURE__*/React.createElement(Conditional, {
    condition: isSubmitting || submissionLoading
  }, /*#__PURE__*/React.createElement(Then, null, /*#__PURE__*/React.createElement(FLowLoadingSpinner, null)), /*#__PURE__*/React.createElement(Else, null, tShort("sell_trade_v2/submit", {
    ns: "web_sell_trade"
  }))))), /*#__PURE__*/React.createElement("div", {
    id: "sell-car-contact-terms",
    className: "w-full flex items-center justify-center gap-0.5"
  }, /*#__PURE__*/React.createElement("span", {
    id: "sell-car-contact-terms-text",
    className: "text-xs md:text-sm font-semibold text-gray-causeway leading-6"
  }, tShort("sell_trade_v2/terms_initial")), /*#__PURE__*/React.createElement("span", {
    id: "sell-car-contact-terms-link",
    className: "text-xs font-semibold md:text-sm text-primary-new leading-6 cursor-pointer",
    onClick: termsModal.setTrue
  }, tShort("sell_trade_v2/terms_click")))))), /*#__PURE__*/React.createElement(Conditional, {
    condition: isMobile
  }, /*#__PURE__*/React.createElement(Then, null, /*#__PURE__*/React.createElement(Drawer.Root, {
    open: termsModal.value,
    onOpenChange: termsModal.setTrue,
    onClose: termsModal.setFalse,
    snapPoints: [1],
    modal: false,
    dismissible: false
  }, /*#__PURE__*/React.createElement(DrawerPortal, null, /*#__PURE__*/React.createElement(Drawer.Overlay, {
    className: "fixed inset-0 bg-black/40 z-[9998]"
  }), /*#__PURE__*/React.createElement(Drawer.Content, {
    className: "bg-white flex flex-col rounded-t-[10px] h-full after:!h-fit mt-24 fixed bottom-0 left-0 right-0 z-[9999] overflow-y-auto"
  }, /*#__PURE__*/React.createElement(TermsContent, {
    title: termsContent.title,
    description: termsContent.description,
    onDismiss: termsModal.setFalse
  }))))), /*#__PURE__*/React.createElement(Else, null, /*#__PURE__*/React.createElement(Dialog, {
    open: termsModal.value,
    onOpenChange: termsModal.toggle
  }, /*#__PURE__*/React.createElement(DialogContent, {
    className: "max-w-md max-h-[80vh] flex flex-col p-0 gap-0",
    haveClose: false
  }, /*#__PURE__*/React.createElement(TermsContent, {
    title: termsContent.title,
    description: termsContent.description,
    onDismiss: termsModal.setFalse
  }))))));
};
var TermsContent = function TermsContent(_ref6) {
  var title = _ref6.title,
    description = _ref6.description,
    onDismiss = _ref6.onDismiss;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-6 overflow-x-scroll"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-between p-4 sticky top-0 bg-white md:rounded-lg"
  }, /*#__PURE__*/React.createElement("h3", {
    className: "font-bold text-xl leading-8 text-primary-new"
  }, title), /*#__PURE__*/React.createElement("div", {
    onClick: onDismiss
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--close-small-outline-rounded",
    width: 6,
    height: 6,
    className: "text-black-primary cursor-pointer"
  }))), /*#__PURE__*/React.createElement("div", {
    className: twMerge("p-4 pt-0 text-[#00143C] leading-6 [&>strong]:text-sm [&>strong]:leading-[25px] [&>strong]:text-primary-new [&>strong]:block [&>strong]:mb-2.5 [&_ol+strong]:mt-10 [&>p]:text-sm [&>p]:text-primary-new [&_p>strong]:text-primary-new [&_p>strong]:text-sm [&_ol]:list-decimal [&_ol]:p-0 [&_ol]:ps-4 [&_ol>li]:text-sm [&_ol>li]:mb-2.5 [&_ol>li]:leading-[22px]", "max-h-[calc(100%-64px)] overflow-y-auto")
  }, /*#__PURE__*/React.createElement(Markdown, null, description))));
};
export default ContactInfoScreen;